import { useState } from 'react';
import { PlusCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

type NumberRangeValue = {
    gte?: number;
    lte?: number;
};

type DataTableNumberRangeFilterProps = {
    title: string;
    value: NumberRangeValue | undefined;
    onChange: (value: NumberRangeValue | undefined) => void;
};

function formatBadge(value: NumberRangeValue): string {
    if (value.gte != null && value.lte != null) return `${value.gte} – ${value.lte}`;
    if (value.gte != null) return `≥ ${value.gte}`;
    if (value.lte != null) return `≤ ${value.lte}`;
    return '';
}

export function DataTableNumberRangeFilter({ title, value, onChange }: DataTableNumberRangeFilterProps) {
    const [isOpen, setIsOpen] = useState(false);
    const isActive = value && (value.gte != null || value.lte != null);

    const handleChange = (field: 'gte' | 'lte', raw: string) => {
        const num = raw === '' ? undefined : parseFloat(raw);
        const newValue = { ...value, [field]: num };
        const hasValue = newValue.gte != null || newValue.lte != null;
        onChange(hasValue ? newValue : undefined);
    };

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className={cn('h-8 border-dashed bg-filter', isActive && 'bg-filter-active')}
                >
                    <PlusCircle className="size-3.5" />
                    {title}
                    {isActive && value && (
                        <>
                            <Separator orientation="vertical" className="mx-1 h-4" />
                            <Badge variant="secondary" className="rounded-sm px-1 font-normal text-xs">
                                {formatBadge(value)}
                            </Badge>
                        </>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px]" align="start">
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground w-8">Min</span>
                        <Input
                            type="number"
                            step="any"
                            value={value?.gte ?? ''}
                            onChange={(e) => handleChange('gte', e.target.value)}
                            placeholder="Min"
                            autoFocus
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground w-8">Max</span>
                        <Input
                            type="number"
                            step="any"
                            value={value?.lte ?? ''}
                            onChange={(e) => handleChange('lte', e.target.value)}
                            placeholder="Max"
                        />
                    </div>
                </div>
                {isActive && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2 w-full text-xs"
                        onClick={() => {
                            onChange(undefined);
                            setIsOpen(false);
                        }}
                    >
                        Effacer
                    </Button>
                )}
            </PopoverContent>
        </Popover>
    );
}
